import { useState } from "react";
import { useEffect } from "react";
import { Fragment } from "react";
import { connect } from "react-redux";

import UserIcon from "@mui/icons-material/AssignmentInd";
import CalendarTodayRoundedIcon from "@mui/icons-material/CalendarTodayRounded";
import QuestionAnswerRoundedIcon from "@mui/icons-material/QuestionAnswerRounded";
import ProctorIcon from "@mui/icons-material/RecordVoiceOver";
import TimelapseRoundedIcon from "@mui/icons-material/TimelapseRounded";
import { darken, Divider, Grid, lighten, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import { format, formatDistance } from "date-fns";
import { jsPDF } from "jspdf";

import constants from "../../assets/constants";
import * as OmniDataAPI from "../../data/omniDataAPI";
import {
  getIdByRelationshipType,
  millisecondsStringToSecondsTimeString,
} from "../../utils/helpers";
import DialogActions from "../ui/omniDialogActions";
import DialogContent from "../ui/omniDialogContent";

const getAnswerTextAsHTML = text => {
  return { __html: `${text}` };
};

const getAnswerText = question => {
  let theReturn = "";
  if (question.answerValue[0] === "unsure") {
    theReturn = "Unsure";
  } else {
    if (question.shortTitle === "subjectDateOfBirth") {
      theReturn = format(new Date(question.answerValue[0]), "MMM dd, yyyy");
    } else if (question.omniID === "ARPSIE24" || question.displayType === "weight") {
      theReturn = `${question.answerValue[0]} lbs.`;
    } else if (question.omniID === "ARPSIE24a" || question.displayType === "height") {
      if (question.answerValue[0] !== "unsure") {
        let heightFeet = !isNaN(parseInt(question.answerValue[0]))
          ? parseInt(question.answerValue[0] / 12)
          : 0;
        let heightInches = !isNaN(parseInt(question.answerValue[0]))
          ? parseInt(question.answerValue[0] % 12)
          : 0;
        theReturn = `${heightFeet}' ${heightInches}"`;
      } else {
        theReturn = question.answerValue[0];
      }
    } else if (question.displayType === "button" || question.displayType === "image") {
      theReturn = question.answerValue.reduce((arr, curr) => {
        let myQuestionText = question.answerOptions.find(({ value }) => value === curr).text;
        arr.push(myQuestionText);
        return arr;
      }, []);
    } else if (question.displayType === "stopwatch") {
      theReturn = `${millisecondsStringToSecondsTimeString(question.answerValue[0])} seconds`;
    } else {
      theReturn = question.answerValue[0];
    }
  }
  return theReturn;
};

const validatePosition = (newPosition, printDoc) => {
  if (newPosition > printDoc.internal.pageSize.getHeight() - 20) {
    printDoc.addPage();
    return 20;
  }
  return newPosition;
};

const printAssessmentResults = (subject, proctor, assessment) => {
  let printDoc = new jsPDF({
    orientation: "p",
    unit: "pt",
    format: "letter",
    hotfixes: ["px_scaling"],
  });

  let currentPosition = 20;
  let marginLeft = 10;
  let pageWidth = printDoc.internal.pageSize.getWidth();
  let primaryFontcolor = "#1F1F1F";
  let secondaryFontcolor = "#2C2F31";
  let answerOptionFontColog = "#606060";

  // Summary
  printDoc.setTextColor(primaryFontcolor);
  printDoc.setFontSize(16);
  printDoc.text(marginLeft, currentPosition, "Summary");

  currentPosition = validatePosition(currentPosition + 8, printDoc);
  printDoc.setDrawColor("#bababa");
  printDoc.line(marginLeft, currentPosition, pageWidth - 10, currentPosition + 1, "DF");
  printDoc.setFontSize(14);
  currentPosition = validatePosition(currentPosition + 24, printDoc);
  marginLeft = 20;
  printDoc.setTextColor(secondaryFontcolor);
  printDoc.text(
    marginLeft,
    currentPosition,
    `Completed: ${format(new Date(assessment.completeDate), "PP")}`,
  );
  printDoc.text(
    printDoc.internal.pageSize.getWidth() / 2 + marginLeft,
    currentPosition,
    `Duration: ${formatDistance(new Date(assessment.startDate), new Date(assessment.completeDate))}`,
  );
  currentPosition = validatePosition(currentPosition + 24, printDoc);
  printDoc.text(
    marginLeft,
    currentPosition,
    `Questions Answered: ${assessment.questions.reduce((numQuestions, currentValue) => {
      if (currentValue.answerDate) {
        ++numQuestions;
      }
      return numQuestions;
    }, 0)}`,
  );
  printDoc.text(
    printDoc.internal.pageSize.getWidth() / 2 + marginLeft,
    currentPosition,
    `Subject: ${subject ? `${subject.omniData.displayName}` : " Unknown"}`,
  );
  currentPosition = validatePosition(currentPosition + 24, printDoc);
  printDoc.text(
    marginLeft,
    currentPosition,
    `Proctor: ${proctor ? `${proctor.omniData.displayName}` : " Unknown"}`,
  );
  currentPosition = validatePosition(currentPosition + 30, printDoc);

  // Questions and Answers
  marginLeft = 10;
  printDoc.setFontSize(16);
  printDoc.setTextColor(primaryFontcolor);
  printDoc.text(marginLeft, currentPosition, "Worksheet Answers");
  currentPosition = validatePosition(currentPosition + 8, printDoc);
  printDoc.line(marginLeft, currentPosition, pageWidth - 10, currentPosition + 1, "DF");
  currentPosition = validatePosition(currentPosition + 24, printDoc);
  marginLeft = 20;
  assessment.questions.map(question => {
    let myAnswer = question.answerValue ? getAnswerText(question) : null;
    if (myAnswer) {
      let marginBottom = question.numberText === "38" || question.numberText === "21" ? 36 : 24;

      printDoc.setFontSize(12);
      printDoc.setTextColor(secondaryFontcolor);
      printDoc.text(marginLeft, currentPosition, `${question.numberText}: ${question.text}`, {
        maxWidth: pageWidth - 40,
      });

      if (question.multiSelectText === "true") {
        currentPosition = validatePosition(currentPosition + 2, printDoc);
        printDoc.setTextColor(answerOptionFontColog);
        for (let index = 0; index < question.answerOptions.length; ++index) {
          currentPosition = validatePosition(currentPosition + 12, printDoc);

          printDoc.text(
            marginLeft * 2,
            currentPosition,
            `•  ${question.answerOptions[index].text}`,
            {
              maxWidth: pageWidth - 40,
            },
          );
        }
      }

      currentPosition = validatePosition(currentPosition + marginBottom, printDoc);

      printDoc.setFontSize(14);
      printDoc.setTextColor(primaryFontcolor);
      printDoc.text(30, currentPosition, `${myAnswer}`, {
        maxWidth: pageWidth - 50,
      });
      currentPosition = validatePosition(currentPosition + 11, printDoc);
      printDoc.line(marginLeft, currentPosition, pageWidth - 20, currentPosition + 0.25, "DF");
      currentPosition = validatePosition(currentPosition + 24, printDoc);
    }
    return printDoc;
  });
  // Debug in window
  // Don't use this in production because you can't set the file name
  // window.open(printDoc.output("bloburl"));
  let assessmentDate = new Date(assessment.completeDate);

  printDoc.save(
    `${subject.omniData.familyName}_${subject.omniData.givenName}_${format(
      assessmentDate,
      `yyyyMdd`,
    )}.pdf`,
  );
};

const WizardAssessmentResults = props => {
  const {
    completedAssessmentODM,
    updatedSubjectODM,
    updatedProctorODM,
    partnerPatients,
    providerPatients,
    userODM,
  } = props;

  const [subjectODM, setSubjectODM] = useState(updatedSubjectODM);
  const [proctorODM, setProctorODM] = useState(updatedProctorODM);

  useEffect(() => {
    if (completedAssessmentODM != null) {
      // getIdByRelationshipType

      if (updatedSubjectODM == null) {
        let subjectID = getIdByRelationshipType(
          completedAssessmentODM.omniRelationships.relatedData,
          constants.omniRelationshipTypes.assessmentSubject,
        );

        let foundODM = null;

        //is it a user
        if (userODM && userODM._id === subjectID) {
          foundODM = userODM;
        }
        //is it a cached partnerPatient
        if (partnerPatients != null && foundODM == null) {
          foundODM = partnerPatients.find(item => item._id === subjectID);
        }
        //is it a cached providerPatient
        if (providerPatients != null && foundODM == null) {
          foundODM = providerPatients.find(item => item._id === subjectID);
        }

        if (foundODM == null) {
          //look it up
          OmniDataAPI.getPatient(subjectID).then(response => {
            if (response != null) {
              setSubjectODM(response);
            }
          });
        } else {
          setSubjectODM(foundODM);
        }
      }

      if (updatedProctorODM == null) {
        let proctorRelationship = completedAssessmentODM.omniRelationships.relatedData.filter(
          obj => obj.omniRelationshipType === constants.omniRelationshipTypes.assessmentProctor,
        );
        proctorRelationship = proctorRelationship[0];

        let foundODM = null;

        //is it a user
        if (userODM && userODM._id === proctorRelationship._id) {
          foundODM = userODM;
        }
        //is it a cached partnerPatient
        if (partnerPatients != null && foundODM == null) {
          foundODM = partnerPatients.find(item => item._id === proctorRelationship._id);
        }
        //is it a cached providerPatient
        if (providerPatients != null && foundODM == null) {
          foundODM = providerPatients.find(item => item._id === proctorRelationship._id);
        }

        if (foundODM == null) {
          //look it up

          if (
            proctorRelationship.type.toLowerCase() ===
            constants.omniRelationshipUserTypes.patient.toLowerCase()
          ) {
            OmniDataAPI.getPatient(proctorRelationship._id).then(response => {
              if (response != null) {
                setProctorODM(response);
              }
            });
          } else if (
            proctorRelationship.type.toLowerCase() ===
            constants.omniRelationshipUserTypes.partner.toLowerCase()
          ) {
            OmniDataAPI.getPartner(proctorRelationship._id).then(response => {
              if (response != null) {
                setProctorODM(response);
              }
            });
          } else if (
            proctorRelationship.type.toLowerCase() ===
            constants.omniRelationshipUserTypes.provider.toLowerCase()
          ) {
            OmniDataAPI.getProvider(proctorRelationship._id).then(response => {
              if (response != null) {
                setProctorODM(response);
              }
            });
          } else if (
            proctorRelationship.type === constants.omniRelationshipUserTypes.administrator
          ) {
            OmniDataAPI.getAdministrator(proctorRelationship._id).then(response => {
              if (response != null) {
                setProctorODM(response);
              }
            });
          }
        } else {
          setProctorODM(foundODM);
        }
      }
    }
  }, [
    completedAssessmentODM,
    updatedSubjectODM,
    updatedProctorODM,
    partnerPatients,
    providerPatients,
    userODM,
    setSubjectODM,
    setProctorODM,
  ]);

  // TODO think about if this is the best way to force a rescore?
  // useEffect(() => {
  //   if (completedAssessmentODM != null && updatedSubjectODM != null) {
  //     ///this is legacy code to score these behind the scenes...

  //     if (
  //       completedAssessmentODM.omniData.omniType === constants.assessmentIDs.phq9 &&
  //       subjectODM.omniData.patientData.scorePHQ9 == null
  //     ) {

  //     }
  //   }
  // }, [completedAssessmentODM, updatedSubjectODM]);

  const onAcceptHandler = () => {
    props.onAccept ? props.onAccept() : props.onClose();
  };

  const theme = useTheme();

  return (
    <Fragment>
      <DialogContent sx={{ userSelect: "none" }}>
        <Fragment>
          <Grid
            id="resultsContainer"
            container
            direction="column"
            spacing={3}
            sx={[
              { marginBottom: 0 },
              completedAssessmentODM.omniData.startDate == null &&
                completedAssessmentODM.omniData.completeDate == null && { display: "none" },
            ]}
          >
            <Grid id="container" item container direction="row" spacing={2} xs={12}>
              <Grid id="summaryContainer" item container xs={12}>
                <Grid item xs={12}>
                  <Typography variant="button">Summary</Typography>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                </Grid>
                <Grid
                  item
                  container
                  direction="row"
                  spacing={1}
                  xs={12}
                  sx={{ marginLeft: theme.spacing(1), marginTop: theme.spacing(0.5) }}
                >
                  <Grid container direction="row" item xs={12} md={6}>
                    <Grid item data-html2canvas-ignore>
                      <CalendarTodayRoundedIcon
                        fontSize="small"
                        sx={{
                          marginRight: theme.spacing(1),
                          width: 24,
                          height: 24,
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Typography
                        sx={{
                          lineHeight: "150%",
                          marginTop: theme.spacing(1),
                          marginBottom: theme.spacing(1),
                        }}
                        variant="overline"
                      >
                        {`Completed: ${format(
                          new Date(completedAssessmentODM.omniData.completeDate),
                          "PP",
                        )}`}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" item xs={12} md={6}>
                    <Grid item data-html2canvas-ignore>
                      <TimelapseRoundedIcon
                        fontSize="small"
                        sx={{
                          marginRight: theme.spacing(1),
                          width: 24,
                          height: 24,
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Typography
                        sx={{
                          lineHeight: "150%",
                          marginTop: theme.spacing(1),
                          marginBottom: theme.spacing(1),
                        }}
                        variant="overline"
                      >
                        Duration:
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography
                        sx={{
                          lineHeight: "150%",
                          marginTop: theme.spacing(1),
                          marginBottom: theme.spacing(1),
                        }}
                        variant="overline"
                      >
                        {formatDistance(
                          new Date(completedAssessmentODM.omniData.startDate),
                          new Date(completedAssessmentODM.omniData.completeDate),
                        )}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" item xs={12} md={6}>
                    <Grid item data-html2canvas-ignore>
                      <QuestionAnswerRoundedIcon
                        fontSize="small"
                        sx={{
                          marginRight: theme.spacing(1),
                          width: 24,
                          height: 24,
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Typography
                        sx={{
                          lineHeight: "150%",
                          marginTop: theme.spacing(1),
                          marginBottom: theme.spacing(1),
                        }}
                        variant="overline"
                      >
                        Questions Answered:
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography
                        sx={{
                          lineHeight: "150%",
                          marginTop: theme.spacing(1),
                          marginBottom: theme.spacing(1),
                        }}
                        variant="overline"
                      >
                        {completedAssessmentODM.omniData.questions.reduce(
                          (numQuestions, currentValue) => {
                            if (currentValue.answerDate) {
                              ++numQuestions;
                            }
                            return numQuestions;
                          },
                          0,
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid container direction="row" item xs={12} md={6}>
                    <Grid item data-html2canvas-ignore>
                      <UserIcon
                        fontSize="small"
                        sx={{
                          marginRight: theme.spacing(1),
                          width: 24,
                          height: 24,
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Typography
                        sx={{
                          lineHeight: "150%",
                          marginTop: theme.spacing(1),
                          marginBottom: theme.spacing(1),
                        }}
                        variant="overline"
                      >
                        Subject:
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography
                        sx={{
                          lineHeight: "150%",
                          marginTop: theme.spacing(1),
                          marginBottom: theme.spacing(1),
                        }}
                        variant="overline"
                      >
                        {subjectODM ? ` ${subjectODM.omniData.displayName}` : " Unknown"}
                      </Typography>
                    </Grid>
                  </Grid>

                  <Grid container direction="row" item xs={12} md={6}>
                    <Grid item data-html2canvas-ignore>
                      <ProctorIcon
                        fontSize="small"
                        sx={{
                          marginRight: theme.spacing(1),
                          width: 24,
                          height: 24,
                        }}
                      />
                    </Grid>
                    <Grid item>
                      <Typography
                        sx={{
                          lineHeight: "150%",
                          marginTop: theme.spacing(1),
                          marginBottom: theme.spacing(1),
                        }}
                        variant="overline"
                      >
                        Proctor:
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography
                        sx={{
                          lineHeight: "150%",
                          marginTop: theme.spacing(1),
                          marginBottom: theme.spacing(1),
                        }}
                        variant="overline"
                      >
                        {proctorODM ? ` ${proctorODM.omniData.displayName}` : " Unknown"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>

              <Grid id="resultsContainer" container direction="row" item>
                <Grid item xs={12}>
                  <Typography variant="button">Worksheet Answers</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid
                  container
                  direction="row"
                  item
                  xs={12}
                  spacing={1}
                  sx={{
                    maxHeight: 419,
                    marginTop: theme.spacing(1),
                    paddingBottom: theme.spacing(2),
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      width: theme.spacing(2),
                      borderRadius: theme.spacing(1),
                    },
                    "&:hover::-webkit-scrollbar": {
                      width: theme.spacing(2),
                    },
                    "&::-webkit-scrollbar-track": {
                      borderRadius: theme.spacing(1),
                      backgroundColor: darken(theme.palette.commonground.main, 0.4),
                      backgroundClip: "content-box",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      opacity: "0.5",
                      borderRadius: theme.spacing(1),
                      backgroundColor: theme.palette.secondary.main,
                    },
                    "&:hover::-webkit-scrollbar-thumb": {
                      backgroundColor: lighten(theme.palette.secondary.main, 0.2),
                    },
                  }}
                >
                  {completedAssessmentODM.omniData.questions.length > 0 &&
                    completedAssessmentODM.omniData.questions.map(
                      (question, index) =>
                        question.answerValue &&
                        question.answerOptions.length > 0 && (
                          <Fragment key={`outer${index}`}>
                            <Grid container id={`question${index}`} key={`question${index}`} item>
                              <Grid container item xs={12} spacing={1}>
                                <Grid item xs={12}>
                                  <Typography
                                    key={question.omniID}
                                    variant="body2"
                                    sx={{ color: theme.palette.info.main }}
                                  >
                                    {question.numberText}: {question.text}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                  <Typography
                                    key={question.omniID + "key1"}
                                    variant="body1"
                                    dangerouslySetInnerHTML={getAnswerTextAsHTML(
                                      getAnswerText(question),
                                    )}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                            <Grid item xs={12}>
                              <Divider />
                            </Grid>
                          </Fragment>
                        ),
                    )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Fragment>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            printAssessmentResults(subjectODM, proctorODM, completedAssessmentODM.omniData);
          }}
          color="secondary"
          variant="contained"
        >
          Print Results
        </Button>
        <Button
          onClick={() => {
            onAcceptHandler();
          }}
          color="secondary"
          variant="contained"
        >
          {completedAssessmentODM._id == null ? "Save" : "Done"}
        </Button>
      </DialogActions>
    </Fragment>
  );
};

const mapStateToProps = state => ({
  partnerPatients: state.partnerSession.partnerPatients,
  providerPatients: state.providerSession.providerPatients,

  userODM: state.userSession.userODM,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(WizardAssessmentResults);
